import type { CurveParams, EffortLevel, RecurrencePattern } from '@app/core';

export interface ExtractedItem {
  text: string;
  rawInput: string;
  category?: string;
  consequence?: string;
  effort?: EffortLevel;
  curveDirection: 'warming' | 'cooling';
  anchorDate?: string;
  recurrence?: {
    type: 'fixed' | 'loose';
    interval?: number;
    unit?: 'days' | 'weeks' | 'months';
    restPeriod?: number;
    rampDuration?: number;
  };
  provenance: AIProvenance;
}

export interface AIProvenance {
  provider: string;
  model: string;
  promptVersion: string;
  inputHash: string;
  confidence: number;
}

export interface ConversationTurn {
  role: 'system' | 'user';
  content: string;
}

export interface ExtractionRequest {
  mode: 'braindump' | 'conversational';
  input: string;
  conversationHistory?: ConversationTurn[];
}

export interface ExtractionResponse {
  items: ExtractedItem[];
  nextQuestion?: string;
  done?: boolean;
}

export async function extractItems(request: ExtractionRequest): Promise<ExtractionResponse> {
  const response = await fetch('/api/extract', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error(`Extraction failed: ${String(response.status)}`);
  }

  return (await response.json()) as ExtractionResponse;
}

/**
 * Builds a CurveParams from an extracted item's data.
 */
export function buildCurveFromExtraction(item: ExtractedItem): CurveParams {
  if (item.curveDirection === 'warming' && item.anchorDate) {
    return {
      direction: 'warming',
      startingTemperature: 20,
      ceiling: 85,
      easing: 'ease-in',
      anchorDate: new Date(item.anchorDate),
    };
  }

  if (item.curveDirection === 'warming') {
    return {
      direction: 'warming',
      startingTemperature: 20,
      ceiling: 70,
      easing: 'ease-in',
      duration: 14,
    };
  }

  return {
    direction: 'cooling',
    startingTemperature: 60,
    easing: 'ease-in',
    duration: 30,
  };
}

/**
 * Builds a RecurrencePattern from an extracted item's recurrence data.
 */
export function buildRecurrenceFromExtraction(item: ExtractedItem): RecurrencePattern | undefined {
  if (!item.recurrence) return undefined;

  if (item.recurrence.type === 'fixed') {
    return {
      type: 'fixed',
      interval: item.recurrence.interval ?? 7,
      unit: item.recurrence.unit ?? 'days',
      curve: {
        direction: 'warming',
        startingTemperature: 15,
        ceiling: 75,
        easing: 'ease-in',
      },
    };
  }

  return {
    type: 'loose',
    restPeriod: item.recurrence.restPeriod ?? 5,
    rampDuration: item.recurrence.rampDuration ?? 21,
    ceiling: 55,
    easing: 'ease-in',
  };
}
